import React from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Card } from 'primereact/card';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';

interface LoginFormProps {
  onSuccess?: () => void;
}

function LoginForm({ onSuccess }: LoginFormProps) {
  const {
    loginStep,
    loginEmail,
    loginOtp,
    loginSession,
    setLoginEmail,
    setLoginOtp,
    setLoginStep,
    initiateLogin,
    login,
    error,
    isLoading,
  } = useCognitoAuth();

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail.trim()) return;

    setIsSubmitting(true);
    try {
      await initiateLogin(loginEmail);
    } catch (err) {
      // Error is handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginOtp.trim() || !loginSession) return;

    setIsSubmitting(true);
    try {
      await login(loginEmail, loginOtp, loginSession);
      onSuccess?.();
    } catch (err) {
      // Error is handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToEmail = () => {
    setLoginStep('email');
    setLoginOtp('');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center surface-ground p-0"
      style={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      <div className=" align-items-center justify-content-center w-full h-full">
        <div className="w-full max-w-2xl mx-auto">
          <Card className="p-10 shadow-8 border-round-3xl mx-4">
            {loginStep === 'otp' ? (
              // OTP Page
              <>
                <div className="text-center mb-8">
                  <h2 className="text-5xl font-bold text-900 mb-6">
                    Enter Verification Code
                  </h2>
                  <p className="text-xl text-600 mb-4">
                    We sent a 6-digit verification code to
                  </p>
                  <p className="text-xl text-900 font-medium">{loginEmail}</p>
                </div>

                <form onSubmit={handleOtpSubmit} className="gap-6">
                  <div className="field p-4">
                    <label
                      htmlFor="otp"
                      className="block text-900 font-medium mb-4 text-xl"
                    >
                      Verification Code
                    </label>
                    <InputText
                      id="otp"
                      name="otp"
                      type="text"
                      autoComplete="one-time-code"
                      required
                      className="w-full text-center text-4xl p-5"
                      placeholder="000000"
                      value={loginOtp}
                      onChange={(e) => {
                        setLoginOtp(
                          e.target.value.replace(/\D/g, '').slice(0, 8),
                        );
                      }}
                      disabled={isSubmitting || isLoading}
                      maxLength={8}
                      autoFocus
                    />
                  </div>

                  {error && (
                    <Message
                      severity="error"
                      text={error}
                      className="w-full text-xl"
                    />
                  )}
                  <div className="p-4">
                    <Button
                      type="submit"
                      label={
                        isSubmitting || isLoading
                          ? 'Verifying...'
                          : 'Verify Code'
                      }
                      disabled={isSubmitting || isLoading || !loginOtp.trim()}
                      className="w-full p-5 text-2xl font-medium mb-3"
                      loading={isSubmitting || isLoading}
                    />
                  </div>
                  <div className="p-4">
                    <Button
                      type="button"
                      label="← Back to Email"
                      onClick={handleBackToEmail}
                      className="w-full p-5 text-2xl"
                      severity="secondary"
                      text
                    />
                  </div>
                </form>
              </>
            ) : (
              // Email Page
              <>
                <div className="text-center mb-8">
                  <h2 className="text-5xl font-bold text-900 mb-6">
                    Sign in to your account
                  </h2>
                  <p className="text-xl text-600">
                    Enter your email to receive a verification code
                  </p>
                </div>

                <form onSubmit={handleEmailSubmit} className="gap-6">
                  <div className="field p-4">
                    <label
                      htmlFor="email"
                      className="block text-900 font-medium mb-4 text-xl"
                    >
                      Email Address
                    </label>
                    <InputText
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="w-full p-5 text-2xl"
                      placeholder="Enter your email address"
                      value={loginEmail}
                      onChange={(e) => {
                        setLoginEmail(e.target.value);
                      }}
                      disabled={isSubmitting || isLoading}
                    />
                  </div>

                  {error && (
                    <Message
                      severity="error"
                      text={error}
                      className="w-full text-xl"
                    />
                  )}
                  <div className="p-4">
                    <Button
                      type="submit"
                      label={
                        isSubmitting || isLoading
                          ? 'Sending...'
                          : 'Send Verification Code'
                      }
                      disabled={isSubmitting || isLoading || !loginEmail.trim()}
                      className="w-full p-5 text-2xl font-medium"
                      loading={isSubmitting || isLoading}
                    />
                  </div>
                </form>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default LoginForm;
